import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Attributes from "@theStellarBlueprint/Components/Attributes";
import DisplayProducts from "@theStellarBlueprint/Components/DisplayProducts";
import { CuttingToolModel } from "@theStellarBlueprint/CustomApiModel";
import loadCuttingToolConfigurator from "@theStellarBlueprint/Handlers/loadCuttingTool";
import CustomApplicationState from "@theStellarBlueprint/Store/CustomApplicationState";
import CuttingToolCategoryHandler from "@theStellarBlueprint/Store/Data/CuttingToolCategory/Handler/CuttingToolCategoryHandler";
import { getCuttingToolConfigState } from "@theStellarBlueprint/Store/Data/CuttingToolConfig/CuttingToolConfigSelector";
import removeCuttingToolConfigSelection from "@theStellarBlueprint/Store/Data/CuttingToolConfig/Handlers/RemoveAllConfig";
import { getCuttingToolCurrentPageState } from "@theStellarBlueprint/Store/Data/CuttingToolPagination/CuttingToolPaginationSelector";
import CuttingToolPageSizeHandler from "@theStellarBlueprint/Store/Data/CuttingToolPagination/Handlers/CuttingToolPageSize";
import CuttingToolPaginationHandler from "@theStellarBlueprint/Store/Data/CuttingToolPagination/Handlers/CuttingToolPagination";
import loadProducts from "@theStellarBlueprint/Store/Data/Products/Handlers/LoadProducts";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";

const mapDispatchToProps = {
    loadCuttingToolConfigurator,
    loadProducts,
    CuttingToolCategoryHandler,
    CuttingToolPaginationHandler,
    removeCuttingToolConfigSelection,
    CuttingToolPageSizeHandler
};

const mapStateToProps = (state: CustomApplicationState) => {
    const getCurrentPage = getCuttingToolCurrentPageState(state);
    const attributes = getCuttingToolConfigState(state);
    return {
        attributes,
        getCurrentPage
    };
};

export interface CategoryDropdownStyles {
    dropDownGridItem?: GridItemProps;
}

type Props = ThemeProps<BaseTheme> &
    HasShellContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export const categoryStyles: CategoryDropdownStyles = {
    dropDownGridItem: {
        width: [12, 12, 12, 6, 6],
    },
};

const styles = categoryStyles;

const CuttingTool: React.FC<Props> = ({ loadCuttingToolConfigurator, loadProducts, attributes,getCurrentPage, CuttingToolCategoryHandler,CuttingToolPaginationHandler,removeCuttingToolConfigSelection,CuttingToolPageSizeHandler }) => {
    const options: OptionObject[] = [];
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const isInitialMount = React.useRef(true);
    const [dropDownOption, setDropDownOption] = useState<any[]>([]);
    const [dropDownSelection, setDropDownSelection] = useState<string>("");
  
    useEffect(() => {
        loadCuttingToolConfigurator({
            onSuccess: result => {
                if (result) {
                    const category = (result as unknown) as CuttingToolModel[];
                    const dropDownNewArr: any[] = [];
                    category.map((value, index) => {
                        dropDownNewArr.push({
                            optionText: value.cat3_name,
                            optionValue: index.toString(),
                            categoryId: value.cat3_id,
                        });
                    });
                    setDropDownOption(dropDownNewArr);
                }
            },
        });
    }, [selectedCategoryId,getCurrentPage]);

    useEffect(() => {
        if (isInitialMount.current) {
            
            CuttingToolCategoryHandler({
                category: selectedCategoryId
            })
            isInitialMount.current = false;
        } else {
            loadProducts({
                categoryId: selectedCategoryId,
                applyPersonalization: true,
                expand: ["attributes", "facets"],
                includeAttributes: ["includeOnProduct"],
                includeSuggestions: true,
                sort: "1",
                pageSize:getCurrentPage.cuttingToolPageSize,
                page: getCurrentPage.cuttingToolPageNumber,
                attributeValueIds: attributes.attributeValueIds,
            });
        }
    }, [attributes,getCurrentPage]);
    const handleOptionChange = (e: any) => {
        const selectedObj = dropDownOption[Number(e)];
        setDropDownSelection(selectedObj.optionValue);
        setSelectedCategoryId(selectedObj.categoryId);
        CuttingToolPaginationHandler({
            cuttingToolPageNumber: 1,
        })
        CuttingToolPageSizeHandler({
            cuttingToolPageSize:8
        })
        removeCuttingToolConfigSelection({
            attributeValueIds: [],
        });
        loadProducts({
            categoryId: selectedObj.categoryId,
            applyPersonalization: true,
            expand: ["attributes", "facets"],
            includeAttributes: ["includeOnProduct"],
            includeSuggestions: true,
            sort: "1",
            page: getCurrentPage.cuttingToolPageNumber,
            pageSize:getCurrentPage.cuttingToolPageSize,
            attributeValueIds: attributes.attributeValueIds,
        });
        CuttingToolCategoryHandler({
            category: selectedObj.categoryId
        })
    };

    const handleOnOpenChange = (e: any) => {
        setDropDownSelection("");
    };

    return (
        <StyledWrapper>
            <StyledWrapper data-test-selector="categoriesContainer">
                <GridContainer>
                    <GridItem {...styles.dropDownGridItem}>
                        {dropDownOption.length > 0 && (
                            <DynamicDropdown
                                label={translate("Search by Tool Type")}
                                placeholder={translate("Select Tool")}
                                options={dropDownOption}
                                onSelectionChange={handleOptionChange}
                                selected={dropDownSelection}
                                onOpen={handleOnOpenChange}
                            />
                        )}
                    </GridItem>
                </GridContainer>

                <Attributes
                    categoryId={selectedCategoryId}
                    attributeValueIds={attributes.attributeValueIds}
                    getCurrentPage={getCurrentPage}
                ></Attributes>
                <DisplayProducts
                    categoryId={selectedCategoryId}
                    attributeValueIds={attributes.attributeValueIds}
                    getCurrentPage={getCurrentPage}
                ></DisplayProducts>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CuttingTool),
    definition,
};

export default widgetModule;
